export type ImagingExamStatus =
  | "scheduled"
  | "pending_insurer"
  | "in_progress"
  | "completed"
  | "cancelled";

export type InsurerApprovalStatus = "none" | "pending" | "approved" | "rejected";

export interface ImagingStructuredReport {
  technique: string;
  findings: string;
  conclusion: string;
}

export interface ImagingExam {
  id: string;
  examNumber: string;
  patientId: string;
  consultationId: string | null;
  prescriberId: string;
  prescriber: string;
  examType: string;
  bodyPart: string;
  clinicalIndication: string;
  scheduledAt: string;
  status: ImagingExamStatus;
  insurerApproval: InsurerApprovalStatus;
  coverageCaseId: string | null;
  reportTechnique: string;
  reportFindings: string;
  reportConclusion: string;
  /** Texte libre — legacy ou synthèse auto. */
  reportText: string;
  reportFileName: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImagingExamInput {
  patientId: string;
  prescriberId: string;
  prescriber?: string;
  consultationId?: string | null;
  examType: string;
  bodyPart?: string;
  clinicalIndication?: string;
  scheduledAt?: string;
}

export type ImagingReportSaveInput = ImagingStructuredReport & {
  reportFileName?: string;
};

export const IMAGING_STATUS_LABELS: Record<ImagingExamStatus, string> = {
  scheduled: "Planifié",
  pending_insurer: "En attente assureur",
  in_progress: "En cours",
  completed: "Terminé",
  cancelled: "Annulé",
};

export const COMMON_IMAGING_TYPES = [
  "Radiographie",
  "Échographie",
  "Scanner (TDM)",
  "IRM",
  "Mammographie",
  "Doppler",
  "Autre",
] as const;

export function imagingExamConsultationLabel(consultationId: string | null): string | null {
  if (!consultationId) return null;
  return `Consultation ${consultationId.replace(/^con-/, "").slice(0, 8)}…`;
}

export function buildImagingReportText(report: ImagingStructuredReport): string {
  const parts: string[] = [];
  if (report.technique.trim()) parts.push(`Technique : ${report.technique.trim()}`);
  if (report.findings.trim()) parts.push(`Constatations : ${report.findings.trim()}`);
  if (report.conclusion.trim()) parts.push(`Conclusion : ${report.conclusion.trim()}`);
  return parts.join("\n\n");
}

export function normalizeImagingStructuredReport(
  raw: Record<string, unknown> | ImagingStructuredReport | null | undefined,
  legacyText = "",
): ImagingStructuredReport {
  if (raw && typeof raw === "object" && ("technique" in raw || "findings" in raw || "conclusion" in raw)) {
    return {
      technique: String(raw.technique ?? ""),
      findings: String(raw.findings ?? ""),
      conclusion: String(raw.conclusion ?? ""),
    };
  }
  if (legacyText.trim()) {
    return { technique: "", findings: legacyText.trim(), conclusion: "" };
  }
  return { technique: "", findings: "", conclusion: "" };
}

export function formatImagingReportSummary(exam: Pick<ImagingExam, "reportConclusion" | "reportFindings" | "reportTechnique" | "reportText" | "status">): string {
  if (exam.status !== "completed") return "En attente de compte-rendu";
  const structured = buildImagingReportText({
    technique: exam.reportTechnique,
    findings: exam.reportFindings,
    conclusion: exam.reportConclusion,
  });
  if (structured.trim()) return structured;
  return exam.reportText.trim() || "Compte-rendu enregistré";
}
